import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import teamMembersData from '../../data/team_members.json';

// Custom navigation component for labs page without logo
function LabsNavigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-lumid' : 'bg-slate-900/80 backdrop-blur-sm'
    }`}>
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Empty space where logo would be */}
          <div className="w-20"></div>

          {/* Desktop navigation links */}
          <div className="hidden md:flex items-center justify-end space-x-6">
            <Link to="/labs" className="nav-link text-sm text-gray-300 hover:text-white transition-colors duration-300 cursor-pointer">
              AI Labs
            </Link>
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={toggleMenu}
            className="md:hidden text-gray-300 hover:text-white transition-colors duration-300 cursor-pointer p-2"
            aria-label="Toggle menu"
          >
            <i className={`text-lg transition-transform duration-300 ${
              isMenuOpen ? 'ri-close-line rotate-180' : 'ri-menu-line'
            }`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        <div className={`md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen 
            ? 'max-h-96 opacity-100 visible' 
            : 'max-h-0 opacity-0 invisible overflow-hidden'
        }`}>
          <div className="py-4 space-y-3 border-t border-slate-700/50">
            <Link 
              to="/labs" 
              className="block px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-all duration-300 cursor-pointer"
              onClick={() => setIsMenuOpen(false)}
            >
              <i className="ri-home-line mr-2"></i>
              AI Labs
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}

// Custom footer component for labs page without navigation links
function LabsFooter() {
  return (
    <footer className="bg-slate-900/80 border-t border-slate-800 relative overflow-hidden">
      {/* Subtle background particles */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-lg"></div>
      
      <div className="relative z-10 max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        {/* Minimal bottom bar */}
        <div className="border-t border-slate-800 pt-6 flex flex-col sm:flex-row justify-between items-center">
          <p className="text-xs text-gray-500 font-light">
            © 2026 All rights reserved.
          </p>
          <div className="flex space-x-4 mt-3 sm:mt-0">
            <a href="#" className="text-xs text-gray-500 hover:text-gray-400 transition-colors duration-300 cursor-pointer">Privacy</a>
            <a href="#" className="text-xs text-gray-500 hover:text-gray-400 transition-colors duration-300 cursor-pointer">Terms</a>
            <a href="https://readdy.ai/?origin=logo" className="text-xs text-gray-500 hover:text-gray-400 transition-colors duration-300 cursor-pointer">
              Made with Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function LabsPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Update page title
    document.title = 'Scalable AI Labs';
    
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    // Add Schema.org JSON-LD for labs page
    setIsLoaded(true);
    // Add Schema.org JSON-LD for labs page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ResearchOrganization",
      "name": "Scalable AI Labs",
      "description": "Cutting-edge AI research laboratory focusing on next-generation AI infrastructures, machine learning systems, and autonomous AI agents.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/labs`,
      "parentOrganization": {
        "@type": "Organization",
        "name": "LUMID"
      },
      "researchField": [
        "Artificial Intelligence",
        "Machine Learning",
        "Reinforcement Learning",
        "AI Infrastructure",
        "Autonomous Systems"
      ]
    });
    
    document.head.appendChild(script);
    
    return () => {
      // Reset title when leaving the page
      document.title = 'LUMID: Light up ideas with next-gen AI infra';
      
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const researchPapers = [
    {
      title: "Reward Model Routing in Alignment",
      authors: "X. Wu, Y. Lu",
      venue: "International Conference on Learning Representations (ICLR)",
      year: "2026",
      abstract: "This paper introduces a novel approach to reward model routing in AI alignment, improving the efficiency and effectiveness of aligning large language models with human preferences.",
      tags: ["AI Alignment", "Reinforcement Learning", "LLMs"],
      citations: 0,
      link: "https://arxiv.org/pdf/2510.02850"
    },
    {
      title: "Speculative Decoding via Hybrid Drafting and Rollback-Aware Branch Parallelism",
      authors: "Y. Shen, J. Shen, Q. Kong, T. Liu, Y. Lu, C. Wang",
      venue: "International Conference on Learning Representations (ICLR)",
      year: "2026",
      abstract: "We introduce a novel speculative decoding framework that combines hybrid drafting techniques with rollback-aware branch parallelism to accelerate large language model inference.",
      tags: ["Machine Learning", "Speculative Decoding", "Parallel Computing"],
      citations: 0,
      link: "https://arxiv.org/pdf/2506.01979.pdf"
    },
    {
      title: "Batch Query Processing and Optimization for Agentic Workflows",
      authors: "J. Shen, N. Wadlom, Y. Lu",
      venue: "arXiv preprint",
      year: "2025",
      abstract: "This paper presents novel approaches for optimizing batch query processing in agentic workflows, enabling more efficient execution of complex AI agent interactions and decision-making processes.",
      tags: ["Agentic Systems", "Query Processing", "Optimization"],
      citations: 0,
      link: "https://arxiv.org/pdf/2509.02121"
    },
    {
      title: "FlowMesh: A Service Fabric for Composable LLM Workflows",
      authors: "J. Shen, N. Wadlom, L. Zhou, D. Wang, X. Miao, L. Fang, Y. Lu",
      venue: "arXiv preprint",
      year: "2025",
      abstract: "FlowMesh presents a service fabric architecture for building composable LLM workflows, enabling flexible orchestration of large language model services.",
      tags: ["LLM Systems", "Service Architecture", "Workflow Management"],
      citations: 0,
      link: "https://arxiv.org/abs/2510.26913"
    },
    {
      title: "Tracer: Efficient object re-identification in networked cameras through adaptive query processing",
      authors: "P. Chunduri, Y. Lu, J. Arulraj",
      venue: "arXiv preprint",
      year: "2025",
      abstract: "Tracer presents an adaptive query processing system for efficient object re-identification across networked camera systems, optimizing both accuracy and computational efficiency.",
      tags: ["Computer Vision", "Object Re-identification", "Distributed Systems"],
      citations: 0,
      link: "https://arxiv.org/pdf/2507.09448.pdf"
    },
    {
      title: "OkraLong: A Flexible Retrieval-Augmented Framework for Long-Text Question Answering",
      authors: "Y. Hui, Y. Lu, H. Zhang",
      venue: "Conference on Empirical Methods in Natural Language Processing (EMNLP) Findings",
      year: "2025",
      abstract: "OkraLong introduces a flexible retrieval-augmented framework specifically designed for handling long-text question answering tasks with improved context understanding and response generation.",
      tags: ["Natural Language Processing", "Question Answering", "Retrieval-Augmented Generation"],
      citations: 0,
      link: "https://aclanthology.org/2025.findings-emnlp.890.pdf"
    },
    {
      title: "Resource Multiplexing in Tuning and Serving Large Language Models",
      authors: "Y. He, H. Yang, Y. Lu, A. Klimovic, G. Alonso",
      venue: "USENIX Annual Technical Conference (ATC)",
      year: "2025",
      abstract: "This work presents innovative resource multiplexing techniques for efficiently tuning and serving large language models, significantly reducing computational overhead while maintaining performance.",
      tags: ["Large Language Models", "Resource Management", "System Optimization"],
      citations: 0,
      link: "https://www.usenix.org/system/files/atc25-he-yongjun.pdf"
    },
    {
      title: "Scalable Complex Event Processing on Video Streams",
      authors: "C. Han, C. Chang, S. Srivastava, Y. Lu, E. Lo",
      venue: "ACM International Conference on Management of Data (SIGMOD)",
      year: "2025",
      abstract: "We propose a scalable framework for complex event processing on video streams, enabling real-time analysis of complex patterns and events in large-scale video data.",
      tags: ["Video Analytics", "Event Processing", "Streaming Systems"],
      citations: 0,
      link: "https://dl.acm.org/doi/pdf/10.1145/3725419"
    },
    {
      title: "UDA: A Benchmark Suite for Retrieval Augmented Generation in Real-world Document Analysis",
      authors: "Y. Hui, Y. Lu, H. Zhang",
      venue: "Thirty-Eighth Annual Conference on Neural Information Processing Systems (NeurIPS)",
      year: "2024",
      abstract: "UDA provides a comprehensive benchmark suite for evaluating retrieval-augmented generation systems in real-world document analysis scenarios, addressing key challenges in practical deployments.",
      tags: ["Benchmarking", "Document Analysis", "Retrieval-Augmented Generation"],
      citations: 0,
      link: "https://openreview.net/pdf?id=MS4oxVfBHn"
    },
    {
      title: "Deferred continuous batching in resource-efficient large language model serving",
      authors: "Y. He, Y. Lu, G. Alonso",
      venue: "4th Workshop on Machine Learning and Systems",
      year: "2024",
      abstract: "This paper introduces deferred continuous batching techniques for resource-efficient serving of large language models, optimizing throughput and latency.",
      tags: ["LLM Serving", "Batching", "Resource Efficiency"],
      citations: 0,
      link: "https://www.research-collection.ethz.ch/bitstream/handle/20.500.11850/669610/FineInfer_EuroMLSys2024.pdf?sequence=1"
    },
    {
      title: "Computing in the Era of Large Generative Models: From Cloud-Native to AI-Native",
      authors: "Y. Lu, et al.",
      venue: "Technical Report",
      year: "2023",
      abstract: "This technical report explores the paradigm shift from cloud-native to AI-native computing architectures in the era of large generative models.",
      tags: ["AI Infrastructure", "Cloud Computing", "Generative Models"],
      citations: 0,
      link: "ai_native.pdf"
    },
    {
      title: "EVA: An End-to-End Exploratory Video Analytics System",
      authors: "G. Kakkar, et al.",
      venue: "7th Workshop on Data Management for End-to-End Machine Learning (DEEM)",
      year: "2023",
      abstract: "EVA presents an end-to-end system for exploratory video analytics, enabling efficient querying and analysis of large video datasets.",
      tags: ["Video Analytics", "Data Management", "Machine Learning"],
      citations: 0,
      link: "https://dl.acm.org/doi/pdf/10.1145/3595360.3595858"
    },
    {
      title: "Serving and Optimizing Machine Learning Workflows on Heterogeneous Infrastructures",
      authors: "Y. Wu, M. Lentz, D. Zhuo, Y. Lu",
      venue: "International Conference on Very Large Data Bases (VLDB)",
      year: "2023",
      abstract: "This work addresses the challenges of serving and optimizing machine learning workflows across heterogeneous computing infrastructures.",
      tags: ["ML Systems", "Heterogeneous Computing", "Workflow Optimization"],
      citations: 0,
      link: "paper-jb.pdf"
    },
    {
      title: "Demonstration of Accelerating Machine Learning Inference Queries with Correlative Proxy Models",
      authors: "Z. Yang, Z. Wang, Y. Huang, F. Gao, Y. Lu, C. Li, X. Wang",
      venue: "International Conference on Very Large Data Bases (VLDB) Demo",
      year: "2022",
      abstract: "This demonstration showcases techniques for accelerating machine learning inference queries using correlative proxy models.",
      tags: ["ML Inference", "Query Optimization", "Proxy Models"],
      citations: 0,
      link: "core_demo.pdf"
    },
    {
      title: "Pre-training Summarization Models of Structured Datasets for Cardinality Estimation",
      authors: "Y. Lu, S. Kandula, A. Konig, S. Chaudhuri",
      venue: "International Conference on Very Large Data Bases (VLDB)",
      year: "2022",
      abstract: "This paper introduces IRIS, a novel approach that pre-trains summarization models on structured datasets to improve cardinality estimation accuracy in database query optimization.",
      tags: ["Database Systems", "Cardinality Estimation", "Machine Learning"],
      citations: 89,
      link: "http://yao.lu/iris.pdf"
    },
    {
      title: "Optimizing Machine Learning Inference Queries with Correlative Proxy Models",
      authors: "Z. Yang, Z. Wang, Y. Huang, Y. Lu, C. Li, X. Wang",
      venue: "International Conference on Very Large Data Bases (VLDB)",
      year: "2022",
      abstract: "This paper presents techniques for optimizing machine learning inference queries through the use of correlative proxy models.",
      tags: ["ML Inference", "Query Optimization", "Database Systems"],
      citations: 0,
      link: "core.pdf"
    },
    {
      title: "Zeus: Efficiently Localizing Actions in Videos using Reinforcement Learning",
      authors: "P. Chunduri, J. Bang, Y. Lu, J. Arulraj",
      venue: "ACM International Conference on Management of Data (SIGMOD)",
      year: "2022",
      abstract: "Zeus presents a reinforcement learning approach for efficiently localizing actions in videos, improving both speed and accuracy.",
      tags: ["Video Analytics", "Reinforcement Learning", "Action Localization"],
      citations: 0,
      link: "https://pchunduri6.github.io/files/chunduri2021zeus.pdf"
    },
    {
      title: "Warper: Efficiently Adapting Learned Cardinality Estimators to Data and Workload Drifts",
      authors: "B. Li, Y. Lu, S. Kandula",
      venue: "ACM International Conference on Management of Data (SIGMOD)",
      year: "2022",
      abstract: "Warper addresses the challenge of adapting learned cardinality estimators to handle data and workload drifts efficiently.",
      tags: ["Cardinality Estimation", "Machine Learning", "Database Systems"],
      citations: 0,
      link: "warper.pdf"
    },
    {
      title: "Q-error Bounds of Random Uniform Sampling for Cardinality Estimation",
      authors: "B. Li, Y. Lu, C. Wang, S. Kandula",
      venue: "arXiv preprint",
      year: "2021",
      abstract: "This paper analyzes the Q-error bounds of random uniform sampling techniques for cardinality estimation in database systems.",
      tags: ["Cardinality Estimation", "Sampling", "Database Theory"],
      citations: 0,
      link: "https://arxiv.org/pdf/2108.02715.pdf"
    },
    {
      title: "Cardinality Estimation: Is Machine Learning a Silver Bullet?",
      authors: "B. Li, Y. Lu, C. Wang, S. Kandula",
      venue: "3rd International Workshop on Applied AI for Database Systems and Applications (AIDB)",
      year: "2021",
      abstract: "This paper critically examines the effectiveness of machine learning approaches for cardinality estimation in database systems.",
      tags: ["Cardinality Estimation", "Machine Learning", "Database Systems"],
      citations: 0,
      link: "aidb21.pdf"
    },
    {
      title: "Approximate Partition Selection for Big-Data Workloads using Summary Statistics",
      authors: "K. Rong, Y. Lu, P. Bailis, S. Kandula, P. Levis",
      venue: "International Conference on Very Large Data Bases (VLDB)",
      year: "2020",
      abstract: "This work presents techniques for approximate partition selection in big-data workloads using summary statistics.",
      tags: ["Big Data", "Partition Selection", "Query Optimization"],
      citations: 0,
      link: "http://www.vldb.org/pvldb/vol13/p2606-rong.pdf"
    },
    {
      title: "Adapting Learned Cardinality Estimators to Data and Workload Drifts",
      authors: "Y. Lu, S. Kandula",
      venue: "US Patent App. 17566996",
      year: "2021",
      abstract: "Patent application for techniques to adapt learned cardinality estimators to handle data and workload drifts.",
      tags: ["Patent", "Cardinality Estimation", "Machine Learning"],
      citations: 0,
      link: "#"
    },
    {
      title: "Building and Accelerating a Declarative Platform for Machine Learning Model Serving",
      authors: "Y. Lu",
      venue: "Doctoral Dissertation, University of Washington",
      year: "2018",
      abstract: "This dissertation presents a comprehensive framework for building and accelerating declarative platforms for machine learning model serving.",
      tags: ["ML Serving", "Dissertation", "Systems"],
      citations: 0,
      link: "thesis.pdf"
    },
    {
      title: "Accelerating Machine Learning Inference with Probabilistic Predicates",
      authors: "Y. Lu, A. Chowdhery, S. Kandula, S. Chaudhuri",
      venue: "ACM International Conference on Management of Data (SIGMOD)",
      year: "2018",
      abstract: "We present a system that accelerates machine learning inference by using probabilistic predicates to selectively process data, achieving significant speedups while maintaining accuracy.",
      tags: ["Machine Learning", "Query Optimization", "Probabilistic Systems"],
      citations: 156,
      link: "http://yao.lu/s18.pdf"
    },
    {
      title: "Precise Temporal Action Localization by Evolving Temporal Proposals",
      authors: "H. Qiu, Y. Zheng, H. Ye, Y. Lu, F. Wang, L. He",
      venue: "ACM International Conference on Multimedia Retrieval (ICMR)",
      year: "2018",
      abstract: "This paper presents a method for precise temporal action localization in videos through evolving temporal proposals.",
      tags: ["Video Analytics", "Action Localization", "Computer Vision"],
      citations: 0,
      link: "https://arxiv.org/abs/1804.04803"
    },
    {
      title: "Crowd Counting with Density Adaption Networks",
      authors: "L. Wang, W. Shao, Y. Lu, H. Ye, J. Pu, Y. Zheng",
      venue: "arXiv preprint",
      year: "2018",
      abstract: "This work introduces density adaption networks for accurate crowd counting in images and videos.",
      tags: ["Computer Vision", "Crowd Counting", "Deep Learning"],
      citations: 0,
      link: "https://arxiv.org/abs/1806.10040"
    },
    {
      title: "Interactive Demonstration of Probabilistic Predicates",
      authors: "Y. Lu, S. Kandula, S. Chaudhuri",
      venue: "ACM International Conference on Management of Data (SIGMOD) Demo",
      year: "2018",
      abstract: "This demonstration showcases an interactive system for probabilistic predicates in machine learning inference. Best Demonstration Award.",
      tags: ["Demo", "Probabilistic Systems", "ML Inference"],
      citations: 0,
      link: "https://dl.acm.org/citation.cfm?id=3193542"
    },
    {
      title: "Accelerating Machine Learning Inference with Probabilistic Predicates",
      authors: "S. Chaudhuri, S. Kandula, Y. Lu",
      venue: "US Patent App. 16/003,495",
      year: "2018",
      abstract: "Patent application for accelerating machine learning inference using probabilistic predicates.",
      tags: ["Patent", "ML Inference", "Probabilistic Systems"],
      citations: 0,
      link: "#"
    },
    {
      title: "UA-DETRAC 2017: Report of AVSS2017 & IWT4S Challenge on Advanced Traffic Monitoring",
      authors: "S. Lyu, et al.",
      venue: "IEEE International Conference on Advanced Video and Signal Based Surveillance (AVSS)",
      year: "2017",
      abstract: "Report on the UA-DETRAC 2017 challenge for advanced traffic monitoring using video analytics.",
      tags: ["Video Analytics", "Traffic Monitoring", "Challenge"],
      citations: 0,
      link: "http://ieeexplore.ieee.org/abstract/document/8078560/"
    },
    {
      title: "Large-Scale Video Classification with Elastic Streaming Sequential Data Processing System",
      authors: "Y. Peng, H. Ye, Y. Lin, Y. Bao, Z. Zhao, H. Qiu, Y. Lu, L. Wang, Y. Zheng",
      venue: "ACM Multimedia Workshop on Large-Scale Video Classification Challenge (LSVC)",
      year: "2017",
      abstract: "This paper presents an elastic streaming system for large-scale video classification.",
      tags: ["Video Classification", "Streaming Systems", "Large-Scale ML"],
      citations: 0,
      link: "http://yao.lu/lsvc17.pdf"
    },
    {
      title: "Evolving Boxes for Fast Vehicle Detection",
      authors: "L. Wang, Y. Lu, H. Wang, Y. Zheng, H. Ye, X. Xue",
      venue: "IEEE International Conference on Multimedia and Expo (ICME)",
      year: "2017",
      abstract: "This work introduces evolving boxes for fast and accurate vehicle detection. Platinum Best Paper Award.",
      tags: ["Computer Vision", "Object Detection", "Vehicle Detection"],
      citations: 0,
      link: "https://arxiv.org/abs/1702.00254"
    },
    {
      title: "Closing the Loop for Object Proposals and Edge Detection",
      authors: "Y. Lu, L. Shapiro",
      venue: "The Thirty-First AAAI Conference on Artificial Intelligence (AAAI)",
      year: "2017",
      abstract: "This paper presents a unified framework that closes the loop between object proposals and edge detection.",
      tags: ["Computer Vision", "Object Detection", "Edge Detection"],
      citations: 0,
      link: "http://yao.lu/aaai17.pdf"
    },
    {
      title: "Optasia: A Relational Platform for Efficient Large-Scale Video Analytics",
      authors: "Y. Lu, A. Chowdhery, S. Kandula",
      venue: "ACM Symposium on Cloud Computing (SoCC)",
      year: "2016",
      abstract: "Optasia provides a relational platform that enables efficient large-scale video analytics by optimizing query execution and resource utilization across distributed video processing pipelines.",
      tags: ["Video Analytics", "Distributed Systems", "Query Processing"],
      citations: 124,
      link: "http://yao.lu/socc16.pdf"
    },
    {
      title: "Coherent Parametric Contours for Interactive Video Object Segmentation",
      authors: "Y. Lu, X. Bai, L. Shapiro, J. Wang",
      venue: "IEEE International Conference on Computer Vision and Pattern Recognition (CVPR)",
      year: "2016",
      abstract: "This paper introduces coherent parametric contours for interactive video object segmentation.",
      tags: ["Computer Vision", "Video Segmentation", "Interactive Systems"],
      citations: 0,
      link: "http://yao.lu/CPC.pdf"
    },
    {
      title: "Flexible video object boundary tracking",
      authors: "X. Bai, J. Wang, Y. Lu",
      venue: "US Patent 9,569,866",
      year: "2017",
      abstract: "Patent for flexible video object boundary tracking techniques.",
      tags: ["Patent", "Video Analytics", "Object Tracking"],
      citations: 0,
      link: "#"
    },
    {
      title: "Semantic Context Learning with Large-Scale Weakly-Labeled Image Set",
      authors: "Y. Lu, W. Zhang, K. Zhang, X. Xue",
      venue: "ACM Conference on Information and Knowledge Management (CIKM)",
      year: "2012",
      abstract: "This work presents methods for learning semantic context from large-scale weakly-labeled image datasets.",
      tags: ["Computer Vision", "Weakly-Supervised Learning", "Image Understanding"],
      citations: 0,
      link: "http://yao.lu/cikm2012.pdf"
    },
    {
      title: "Learning Attention Map from Images",
      authors: "Y. Lu, W. Zhang, C. Jin, X. Xue",
      venue: "IEEE International Conference on Computer Vision and Pattern Recognition (CVPR)",
      year: "2012",
      abstract: "This paper introduces methods for learning attention maps from images to identify salient regions.",
      tags: ["Computer Vision", "Attention Mechanisms", "Saliency Detection"],
      citations: 0,
      link: "http://yao.lu/cvpr12.pdf"
    },
    {
      title: "Automatic Image Annotation with Weakly Labeled Datasets",
      authors: "W. Zhang, Y. Lu, X. Xue, J. Fan",
      venue: "ACM Multimedia",
      year: "2011",
      abstract: "This work addresses automatic image annotation using weakly labeled datasets.",
      tags: ["Computer Vision", "Image Annotation", "Weakly-Supervised Learning"],
      citations: 0,
      link: "http://yao.lu/mm11.pdf"
    },
    {
      title: "Correlative Multi-Label Multi-Instance Image Annotation",
      authors: "X. Xue, W. Zhang, J. Zhang, B. Wu, J. Fan, Y. Lu",
      venue: "13th International Conference on Computer Vision (ICCV)",
      year: "2011",
      abstract: "This paper presents correlative multi-label multi-instance learning for image annotation.",
      tags: ["Computer Vision", "Multi-Label Learning", "Image Annotation"],
      citations: 0,
      link: "http://yao.lu/iccv11anno.pdf"
    },
    {
      title: "Salient Object Detection using Concavity Context",
      authors: "Y. Lu, W. Zhang, H. Lu, X. Xue",
      venue: "13th IEEE International Conference on Computer Vision (ICCV)",
      year: "2011",
      abstract: "This work introduces concavity context for salient object detection in images.",
      tags: ["Computer Vision", "Saliency Detection", "Object Detection"],
      citations: 0,
      link: "http://yao.lu/iccv11conc.pdf"
    }
  ];

  const projects = [
    {
      title: "AI-Native Data Lakehouse",
      description: "Highly efficient, LLM-powered data analytics platform for business insights and decision making with advanced AI capabilities and natural language query processing.",
      status: "Active",
      image: "https://readdy.ai/api/search-image?query=Comprehensive%20data%20lakehouse%20architecture%20diagram%20showing%20data%20flow%20from%20multiple%20sources%20through%20AI%20processing%20layers%20to%20analytics%20dashboards%2C%20modern%20technical%20illustration%20with%20clean%20blue%20and%20white%20design%2C%20showing%20data%20ingestion%2C%20storage%2C%20AI%20processing%2C%20and%20visualization%20layers%20connected%20by%20flowing%20arrows%2C%20professional%20enterprise%20software%20architecture%20visualization%20with%20clear%20component%20separation%20and%20modern%20UI%20elements&width=400&height=250&seq=lakehouse-architecture-overview&orientation=landscape",
      link: "/lakehouse"
    },
    {
      title: "Next-Gen AI Mesh",
      description: "Scalable distributed systems to empower the whole lifecycle of RL and agentic workflows with advanced infrastructure capabilities and intelligent workload balancing.",
      status: "Active",
      image: "https://readdy.ai/api/search-image?query=Comprehensive%20reinforcement%20learning%20infrastructure%20diagram%20showing%20distributed%20training%20clusters%2C%20environment%20simulators%2C%20data%20labeling%20systems%2C%20and%20deployment%20pipelines%2C%20modern%20technical%20illustration%20with%20clean%20blue%20and%20green%20design%2C%20showing%20RL%20agents%2C%20neural%20networks%2C%20and%20cloud%20infrastructure%20connected%20by%20flowing%20data%20streams%2C%20professional%20enterprise%20software%20architecture%20visualization&width=400&height=250&seq=rl-infrastructure-overview&orientation=landscape",
      link: "/rl-infrastructure"
    },
    {
      title: "Sustainable AI Infrastructure",
      description: "Building energy-efficient AI systems that minimize environmental impact while maximizing computational performance.",
      status: "Active",
      image: "https://readdy.ai/api/search-image?query=Green%20sustainable%20AI%20data%20center%20with%20renewable%20energy%20sources%2C%20solar%20panels%20and%20wind%20turbines%20powering%20advanced%20computing%20infrastructure%2C%20eco-friendly%20technology%20concept%20with%20natural%20green%20and%20blue%20tones%2C%20modern%20clean%20design&width=400&height=250&seq=sustainable-ai-project&orientation=landscape"
    },
    {
      title: "Multi-Agent Coordination Framework",
      description: "Developing frameworks for coordinating thousands of AI agents in complex, dynamic environments.",
      status: "Beta Testing",
      image: "https://readdy.ai/api/search-image?query=Multiple%20AI%20agents%20coordination%20network%20visualization%2C%20interconnected%20autonomous%20systems%20working%20together%2C%20dynamic%20communication%20patterns%20with%20glowing%20connection%20lines%2C%20sophisticated%20multi-agent%20architecture%20with%20blue%20and%20cyan%20lighting%20effects&width=400&height=250&seq=multiagent-project&orientation=landscape"
    }
  ];

  // Prepare team members from JSON data
  const teamMembers = {
    pis: teamMembersData.principalInvestigators.map(member => ({
      ...member,
      affiliation: member.affiliation || "National University of Singapore"
    })),
    collaborators: teamMembersData.collaborators,
    associates: teamMembersData.associates.map(member => ({
      ...member,
      affiliation: member.affiliation || "National University of Singapore"
    })),
    phd: teamMembersData.students.phd.map(member => ({
      ...member,
      affiliation: "National University of Singapore"
    })),
    masters: teamMembersData.students.masters.map(member => ({
      ...member,
      affiliation: "National University of Singapore"
    })),
    undergraduate: teamMembersData.students.undergraduate.map(member => ({
      ...member,
      affiliation: "National University of Singapore"
    })),
    alumni: teamMembersData.alumni.map(member => ({
      ...member,
      affiliation: member.affiliation || "National University of Singapore"
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      <LabsNavigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-8 bg-gradient-to-br from-blue-50 to-indigo-100 relative overflow-hidden">
        <div className="absolute inset-0 bg-white/20 bg-[radial-gradient(circle_at_1px_1px,rgba(59,130,246,0.15)_1px,transparent_0)] bg-[length:20px_20px]"></div>
        
        {/* Floating particles */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute w-2 h-2 top-1/4 left-1/5 bg-blue-400/60 rounded-full animate-float"></div>
          <div className="absolute w-3 h-3 bottom-1/3 right-1/4 bg-indigo-400/60 rounded-full animate-bounce-gentle animation-delay-800"></div>
          <div className="absolute w-1.5 h-1.5 top-2/3 left-1/6 bg-blue-500/60 rounded-full animate-gentle-pulse animation-delay-1500"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className={`transform transition-all duration-1000 ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'} animation-delay-200`}>
            {/* White space element removed entirely */}
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-8 bg-white sticky top-16 z-40 backdrop-blur-sm border-b border-gray-300 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex justify-center">
            <div className="flex flex-col sm:flex-row bg-gray-200 rounded-2xl sm:rounded-full p-1 backdrop-blur-sm shadow-inner w-full sm:w-auto justify-center sm:justify-start">
              {[
                { id: 'overview', label: 'Overview', icon: 'ri-eye-line' },
                { id: 'research', label: 'Publications', icon: 'ri-microscope-line' },
                { id: 'team', label: 'Team', icon: 'ri-team-line' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 rounded-2xl sm:rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-300 flex items-center space-x-2 w-full sm:w-auto ${
                    activeTab === tab.id
                      ? 'bg-purple-700 text-white shadow-lg'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-300/70'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16 bg-gray-50 relative">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          
          {/* About Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-12">
              <div className="text-center max-w-4xl mx-auto">
                <p className="text-lg text-gray-700 leading-relaxed mb-12 font-light">
                  Pioneering the future of AI through cutting-edge research in software infrastructures, data science, AI modeling, and AI-for-X applications. Our interdisciplinary team works collaboratively with top global researchers and institutions to develop next-generation AI systems that are powerful and trustworthy.
                </p>
              </div>

              {/* Research Community Section - Image Only */}
              <div className="bg-white rounded-xl p-8 shadow-md border border-gray-300">
                <div className="relative">
                  <img 
                    src="https://static.readdy.ai/image/a4672a1bd1245959387356f184cdd87f/d404df344908a5607cb84837a9393612.jpeg"
                    alt="LUMID AI Labs Team"
                    className="w-full rounded-lg shadow-lg object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-lg"></div>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600 font-medium">
                    Jan 2026 Group Photo with FYP and UROP Students
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Research Papers Tab */}
          {activeTab === 'research' && (
            <div className="space-y-8">
              <div className="grid gap-6">
                {researchPapers.slice(0, 4).map((paper, index) => (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-md border border-gray-300 hover:shadow-lg hover:scale-[1.01] transition-all duration-300">
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 leading-tight hover:text-blue-700 transition-all duration-300 cursor-pointer">
                            {paper.title}
                          </h3>
                          <div className="flex items-center space-x-3 ml-4">
                            <span className="text-sm text-gray-600 flex items-center font-medium">
                              <i className="ri-quote-text mr-1"></i>
                              {paper.citations}
                            </span>
                            <span className="text-sm text-gray-500 font-medium">{paper.year}</span>
                          </div>
                        </div>
                        
                        <p className="text-sm text-blue-700 mb-2 font-semibold">{paper.authors}</p>
                        <p className="text-sm text-gray-600 mb-3 italic font-medium">{paper.venue}</p>
                        
                        <p className="text-gray-700 text-sm leading-relaxed mb-4">
                          {paper.abstract}
                        </p>
                        
                        <div className="flex flex-wrap gap-2 mb-4">
                          {paper.tags.map((tag, tagIndex) => (
                            <span key={tagIndex} className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-300 font-medium">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                      <div className="flex items-center space-x-4">
                        <button className="text-blue-700 hover:text-blue-800 text-sm flex items-center space-x-1 cursor-pointer transition-colors duration-300 font-medium">
                          <i className="ri-file-download-line"></i>
                          <span>Download PDF</span>
                        </button>
                        <button className="text-gray-600 hover:text-gray-800 text-sm flex items-center space-x-1 cursor-pointer transition-colors duration-300 font-medium">
                          <i className="ri-links-line"></i>
                          <span>View Details</span>
                        </button>
                      </div>
                      <button className="text-gray-500 hover:text-yellow-600 cursor-pointer transition-colors duration-300">
                        <i className="ri-bookmark-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* View More Publications Link */}
              <div className="text-center pt-8">
                <a 
                  href="https://yao.lu/publications.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-8 py-4 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer font-medium whitespace-nowrap"
                >
                  <span>View All Publications</span>
                  <i className="ri-arrow-right-line text-lg"></i>
                </a>
              </div>
            </div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && (
            <div className="space-y-12">
              {/* Principal Investigators */}
              <div className="space-y-8">
                <div className="text-center">
                  <h3 className="text-xl font-artistic font-light text-gray-900 mb-2">
                    Principal Investigators
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-8">
                  {teamMembers.pis.map((member, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="relative mb-6">
                        <img 
                          src={member.image}
                          alt={member.name}
                          className="w-24 h-24 rounded-full mx-auto object-cover object-top shadow-lg"
                        />
                        <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center">
                          <i className="ri-verified-badge-fill text-white text-sm"></i>
                        </div>
                      </div>
                      
                      <h4 className="text-base font-semibold text-gray-900 mb-1 hover:text-blue-700 transition-all duration-300 cursor-pointer">
                        {member.name}
                      </h4>
                      <p className="text-blue-700 text-sm font-semibold mb-2">{member.title}</p>
                      <p className="text-gray-600 text-sm mb-4 font-medium">{member.affiliation}</p>
                      
                      <div className="flex flex-wrap gap-1 justify-center">
                        {member.expertise.map((skill, skillIndex) => (
                          <span key={skillIndex} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full border border-blue-300 font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Collaborators */}
              <div className="space-y-8">
                <div className="text-center">
                  <h3 className="text-xl font-artistic font-light text-gray-900 mb-2">
                    Collaborators
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {teamMembers.collaborators.map((member, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="relative mb-6">
                        <img 
                          src={member.image}
                          alt={member.name}
                          className="w-24 h-24 rounded-full mx-auto object-cover object-top shadow-lg"
                        />
                        <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-purple-700 rounded-full flex items-center justify-center">
                          <i className="ri-team-fill text-white text-sm"></i>
                        </div>
                      </div>
                      
                      <h4 className="text-base font-semibold text-gray-900 mb-1 hover:text-purple-700 transition-all duration-300 cursor-pointer">
                        {member.name}
                      </h4>
                      <p className="text-purple-700 text-sm font-semibold mb-2">{member.title}</p>
                      <p className="text-gray-600 text-sm mb-4 font-medium">{member.affiliation}</p>
                      
                      <div className="flex flex-wrap gap-1 justify-center">
                        {member.expertise.map((skill, skillIndex) => (
                          <span key={skillIndex} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full border border-purple-300 font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Research Associates & Fellows */}
              <div className="space-y-8">
                <div className="text-center">
                  <h3 className="text-xl font-artistic font-light text-gray-900 mb-2">
                    Research Associates & Fellows
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {teamMembers.associates.map((member, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="relative mb-6">
                        <img 
                          src={member.image}
                          alt={member.name}
                          className="w-20 h-20 rounded-full mx-auto object-cover object-top shadow-lg"
                        />
                        <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-green-700 rounded-full flex items-center justify-center">
                          <i className="ri-user-star-fill text-white text-xs"></i>
                        </div>
                      </div>
                      
                      <h4 className="text-sm font-semibold text-gray-900 mb-1 hover:text-green-700 transition-all duration-300 cursor-pointer">
                        {member.name}
                      </h4>
                      <p className="text-green-700 text-xs font-semibold mb-2">{member.title}</p>
                      <p className="text-gray-600 text-xs mb-3 font-medium">{member.affiliation}</p>
                      
                      <div className="flex flex-wrap gap-1 justify-center">
                        {member.expertise.map((skill, skillIndex) => (
                          <span key={skillIndex} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full border border-green-300 font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* PhD Students */}
              <div className="space-y-8">
                <div className="text-center">
                  <h3 className="text-xl font-artistic font-light text-gray-900 mb-2">
                    PhD Students
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {teamMembers.phd.map((member, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="relative mb-6">
                        <img 
                          src={member.image}
                          alt={member.name}
                          className="w-20 h-20 rounded-full mx-auto object-cover object-top shadow-lg"
                        />
                        <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-orange-700 rounded-full flex items-center justify-center">
                          <i className="ri-graduation-cap-fill text-white text-xs"></i>
                        </div>
                      </div>
                      
                      <h4 className="text-sm font-semibold text-gray-900 mb-1 hover:text-orange-700 transition-all duration-300 cursor-pointer">
                        {member.name}
                      </h4>
                      <p className="text-orange-700 text-xs font-semibold mb-2">{member.title}</p>
                      <p className="text-gray-600 text-xs mb-3 font-medium">{member.affiliation}</p>
                      
                      <div className="flex flex-wrap gap-1 justify-center">
                        {member.expertise.map((skill, skillIndex) => (
                          <span key={skillIndex} className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full border border-orange-300 font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Master Students */}
              <div className="space-y-8">
                <div className="text-center">
                  <h3 className="text-xl font-artistic font-light text-gray-900 mb-2">
                    Master Students
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {teamMembers.masters.map((member, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="relative mb-6">
                        <img 
                          src={member.image}
                          alt={member.name}
                          className="w-20 h-20 rounded-full mx-auto object-cover object-top shadow-lg"
                        />
                        <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-teal-700 rounded-full flex items-center justify-center">
                          <i className="ri-book-open-fill text-white text-xs"></i>
                        </div>
                      </div>
                      
                      <h4 className="text-sm font-semibold text-gray-900 mb-1 hover:text-teal-700 transition-all duration-300 cursor-pointer">
                        {member.name}
                      </h4>
                      <p className="text-teal-700 text-xs font-semibold mb-2">{member.title}</p>
                      <p className="text-gray-600 text-xs mb-3 font-medium">{member.affiliation}</p>
                      
                      <div className="flex flex-wrap gap-1 justify-center">
                        {member.expertise.map((skill, skillIndex) => (
                          <span key={skillIndex} className="px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-full border border-teal-300 font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Undergraduate Students */}
              <div className="space-y-8">
                <div className="text-center">
                  <h3 className="text-xl font-artistic font-light text-gray-900 mb-2">
                    Undergraduate Students
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {teamMembers.undergraduate.map((member, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="relative mb-6">
                        <img 
                          src={member.image}
                          alt={member.name}
                          className="w-20 h-20 rounded-full mx-auto object-cover object-top shadow-lg"
                        />
                        <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-cyan-700 rounded-full flex items-center justify-center">
                          <i className="ri-user-smile-fill text-white text-xs"></i>
                        </div>
                      </div>
                      
                      <h4 className="text-sm font-semibold text-gray-900 mb-1 hover:text-cyan-700 transition-all duration-300 cursor-pointer">
                        {member.name}
                      </h4>
                      <p className="text-cyan-700 text-xs font-semibold mb-2">{member.title}</p>
                      <p className="text-gray-600 text-xs mb-3 font-medium">{member.affiliation}</p>
                      
                      <div className="flex flex-wrap gap-1 justify-center">
                        {member.expertise.map((skill, skillIndex) => (
                          <span key={skillIndex} className="px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded-full border border-cyan-300 font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Alumni */}
              <div className="space-y-8">
                <div className="text-center">
                  <h3 className="text-xl font-artistic font-light text-gray-900 mb-2">
                    Alumni
                  </h3>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {teamMembers.alumni.map((member, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md border border-gray-300 hover:shadow-lg hover:scale-[1.02] transition-all duration-300">
                      <div className="relative mb-6">
                        <img 
                          src={member.image}
                          alt={member.name}
                          className="w-20 h-20 rounded-full mx-auto object-cover object-top shadow-lg"
                        />
                        <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-gray-700 rounded-full flex items-center justify-center">
                          <i className="ri-medal-fill text-white text-xs"></i>
                        </div>
                      </div>
                      
                      <h4 className="text-sm font-semibold text-gray-900 mb-1 hover:text-gray-700 transition-all duration-300 cursor-pointer">
                        {member.name}
                      </h4>
                      <p className="text-gray-700 text-xs font-semibold mb-2">{member.title}</p>
                      <p className="text-gray-600 text-xs mb-3 font-medium">{member.affiliation}</p>
                      
                      <div className="flex flex-wrap gap-1 justify-center">
                        {member.expertise.map((skill, skillIndex) => (
                          <span key={skillIndex} className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full border border-gray-300 font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <LabsFooter />
    </div>
  );
}
